import React from 'react'
import Modal from 'react-modal'
import styled from 'styled-components';
import { NavLink, useHistory } from "react-router-dom";
import { useEffect, useState } from "react";
import CloseIcon from '@mui/icons-material/Close';
import { IoMdAdd } from 'react-icons/io'
import { AiOutlineMinus } from 'react-icons/ai';
import { useAuthToken } from "../TokenContext";
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from '@mui/material';
import {
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  Fade
} from '@mui/material';
import { FaEnvelope, FaPlus, FaMinus, FaPaperPlane } from 'react-icons/fa';

const Popup = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [modal, setModal] = useState(false);
  const { isLoggedIn, user } = useSelector(state => state.auth);
  const [removebutton, setRemovebutton] = useState(0);
  const [invite, setInvite] = useState([{ emails: '' }]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
    setInvite(prev => {
      const newList = [...prev];
      newList[index] = { ...newList[index], [name]: value };
      return newList;
    });

    // Clear error for this field
    if (errors[index]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    }
  }

  const token = useAuthToken();
  const intl = useIntl();

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleRemove = (index) => {
    /* istanbul ignore next */
    if (invite.length === 1) {
      setSnackbar({
        open: true,
        message: 'At least one email field is required',
        severity: 'warning'
      });
      return;
    }

    const list = [...invite];
    list.splice(index, 1);
    setInvite(list);

    // Remove error for this index and adjust remaining errors
    const newErrors = { ...errors };
    delete newErrors[index];
    // Shift error indices down
    Object.keys(newErrors).forEach(key => {
      const keyNum = parseInt(key);
      if (keyNum > index) {
        newErrors[keyNum - 1] = newErrors[keyNum];
        delete newErrors[keyNum];
      }
    });
    setErrors(newErrors);
  }

  const handleAddClick = () => {
    /* istanbul ignore next */
    if (invite.length >= 3) {
      setSnackbar({
        open: true,
        message: 'Maximum limit is 3 invitations',
        severity: 'warning'
      });
      return;
    }

    setInvite([...invite, { emails: '' }]);
  }

  useEffect(() => {
    generatePreSignedUrl();
  }, [token]);

  const generatePreSignedUrl = async () => {
    try {
      const response = await fetch('https://7549jb05eg.execute-api.us-east-1.amazonaws.com/prod/fetchCredweb', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("API response data:", data);
      return data;
    } catch (error) {
      console.error("Error generating signed URL:", error);
      return null;
    }
  };

  const handleSubmitInvite = async (e) => {
    e.preventDefault();

    // Validate all emails
    const newErrors = {};
    let hasErrors = false;

    invite.forEach((item, index) => {
      if (!item.emails.trim()) {
        newErrors[index] = 'Email is required';
        hasErrors = true;
      } else if (!validateEmail(item.emails.trim())) {
        newErrors[index] = 'Please enter a valid email address';
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setErrors(newErrors);
      setSnackbar({
        open: true,
        message: 'Please fix the email validation errors',
        severity: 'error'
      });
      return;
    }

    // Check for duplicate emails
    const emails = invite.map(item => item.emails.trim().toLowerCase());
    const uniqueEmails = [...new Set(emails)];

    if (emails.length !== uniqueEmails.length) {
      setSnackbar({
        open: true,
        message: 'Duplicate emails are not allowed',
        severity: 'error'
      });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(process.env.REACT_APP_CLIENT_EMAILINVITE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify({
          invite,
          emp_id: user.data[0].emp_id
        })
      });

      const json = await response.json();

      if (json.status === 200) {
        setSnackbar({
          open: true,
          message: 'Invitations sent successfully!',
          severity: 'success'
        });

        // Reset form
        setInvite([{ emails: '' }]);
        setErrors({});

        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setSnackbar({
          open: true,
          message: json.data || 'Failed to send invitations',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error sending invitations:', error);
      setSnackbar({
        open: true,
        message: 'Network error. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <>
      <Container isDarkMode={isDarkMode}>
        <Header isDarkMode={isDarkMode}>
          <HeaderIcon isDarkMode={isDarkMode}>
            <FaEnvelope />
          </HeaderIcon>
          <HeaderTitle isDarkMode={isDarkMode}>
            {intl.formatMessage({ id: 'enterEmailToInvite' })}
          </HeaderTitle>
          <HeaderSubtitle isDarkMode={isDarkMode}>
            Send invitations to join your team
          </HeaderSubtitle>
        </Header>

        <Form onSubmit={handleSubmitInvite}>
          {invite.map((x, i) => (
            <EmailCard key={i} isDarkMode={isDarkMode}>
              <InputWrapper>
                <EmailInput
                  type="email"
                  placeholder={`Email address ${i + 1}`}
                  name='emails'
                  value={x.emails}
                  onChange={e => handleInputChange(e, i)}
                  autoFocus={i === 0}
                  required
                  isDarkMode={isDarkMode}
                  hasError={!!errors[i]}
                />
                {errors[i] && (
                  <ErrorText isDarkMode={isDarkMode}>
                    {errors[i]}
                  </ErrorText>
                )}
              </InputWrapper>

              <ButtonGroup>
                {invite.length - 1 === i && invite.length < 3 && (
                  <Tooltip title="Add another email" TransitionComponent={Fade}>
                    <ActionButton
                      type="button"
                      onClick={handleAddClick}
                      isDarkMode={isDarkMode}
                      variant="add"
                    >
                      <FaPlus />
                    </ActionButton>
                  </Tooltip>
                )}

                {invite.length > 1 && (
                  <Tooltip title="Remove this email" TransitionComponent={Fade}>
                    <ActionButton
                      type="button"
                      onClick={() => handleRemove(i)}
                      isDarkMode={isDarkMode}
                      variant="remove"
                    >
                      <FaMinus />
                    </ActionButton>
                  </Tooltip>
                )}
              </ButtonGroup>
            </EmailCard>
          ))}

          <SubmitButton
            type="submit"
            disabled={loading}
            isDarkMode={isDarkMode}
          >
            {loading ? (
              <>
                <CircularProgress size={20} color="inherit" />
                <span style={{ marginLeft: '0.5rem' }}>Sending...</span>
              </>
            ) : (
              <>
                <FaPaperPlane />
                <span style={{ marginLeft: '0.5rem' }}>
                  {intl.formatMessage({ id: 'invite' })}
                </span>
              </>
            )}
          </SubmitButton>
        </Form>

        <EmailCounter isDarkMode={isDarkMode}>
          {invite.length} of 3 invitations
        </EmailCounter>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default Popup;

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;
  max-width: 500px;
  padding: 2rem;
  background-color: ${props => props.isDarkMode ? '#1e1e1e' : '#ffffff'};
  border-radius: 20px;
  box-shadow: ${props => props.isDarkMode
    ? '0 8px 32px rgba(0, 0, 0, 0.4)'
    : '0 8px 32px rgba(0, 0, 0, 0.1)'};
  border: 1px solid ${props => props.isDarkMode ? '#333333' : '#e9ecef'};
  transition: all 0.3s ease;

  @media screen and (max-width: 768px) {
    max-width: 90vw;
    padding: 1.5rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const HeaderIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem auto;
  font-size: 1.5rem;
  color: white;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
`;

const HeaderTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.isDarkMode ? '#ffffff' : '#2c3e50'};
  margin-bottom: 0.5rem;
  font-family: 'Poppins', sans-serif;

  @media screen and (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const HeaderSubtitle = styled.p`
  color: ${props => props.isDarkMode ? '#b0b0b0' : '#6c757d'};
  font-size: 1rem;
  margin: 0;
`;

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const EmailCard = styled.div`
  background-color: ${props => props.isDarkMode ? '#2c2c2c' : '#f8f9fa'};
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid ${props => props.isDarkMode ? '#404040' : '#e9ecef'};
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: ${props => props.isDarkMode
    ? '0 4px 15px rgba(0, 0, 0, 0.3)'
    : '0 4px 15px rgba(0, 0, 0, 0.1)'};
  }

  @media screen and (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const InputWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const EmailInput = styled.input`
  width: 100%;
  height: 3rem;
  padding: 0 1rem;
  border: 2px solid ${props => props.hasError
    ? '#f44336'
    : props.isDarkMode ? '#404040' : '#dee2e6'};
  border-radius: 8px;
  background-color: ${props => props.isDarkMode ? '#1e1e1e' : '#ffffff'};
  color: ${props => props.isDarkMode ? '#ffffff' : '#2c3e50'};
  font-size: 1rem;
  font-weight: 500;
  font-family: 'Poppins', sans-serif;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#f44336' : '#667eea'};
    box-shadow: 0 0 0 3px ${props => props.hasError
    ? 'rgba(244, 67, 54, 0.1)'
    : 'rgba(102, 126, 234, 0.1)'};
  }

  &::placeholder {
    color: ${props => props.isDarkMode ? '#b0b0b0' : '#6c757d'};
  }
`;

const ErrorText = styled.span`
  color: #f44336;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  font-weight: 500;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;

  @media screen and (max-width: 768px) {
    justify-content: center;
  }
`;

const ActionButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  
  ${props => props.variant === 'add' && `
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
    }
  `}
  
  ${props => props.variant === 'remove' && `
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(239, 68, 68, 0.3);
    }
  `}

  &:active {
    transform: translateY(0);
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  height: 3.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1rem;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const EmailCounter = styled.div`
  margin-top: 1rem;
  color: ${props => props.isDarkMode ? '#b0b0b0' : '#6c757d'};
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
`;